import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Clock, Sparkles, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { api, type ExamResultsResponse, type PracticeQuestion } from "@/lib/api";
import { useWorkspace } from "@/stores/workspace";
import { useAttempts } from "@/stores/attempts";
import { useSession } from "@/stores/session";
import { saveAttempt } from "@/lib/attempts-remote";
import { cn } from "@/lib/cn";

type Phase = "loading" | "exam" | "section_break" | "results";

interface Props {
  examId?: string;
  preSession?: string;
  onExit?: () => void;
}

export function MockExamSession({ examId, preSession, onExit }: Props) {
  const back = onExit ?? (() => undefined);
  const [sessionId, setSessionId] = useState<string | null>(preSession ?? null);
  const [phase, setPhase] = useState<Phase>("loading");
  const [question, setQuestion] = useState<PracticeQuestion | null>(null);
  const [questionNumber, setQuestionNumber] = useState(1);
  const [selected, setSelected] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{
    correct: boolean;
    correctAnswer?: string;
  } | null>(null);
  const [results, setResults] = useState<ExamResultsResponse | null>(null);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [explaining, setExplaining] = useState(false);
  const [sectionBreakLabel, setSectionBreakLabel] = useState("");
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [questionStarted, setQuestionStarted] = useState(Date.now());

  const start = useMutation({
    mutationFn: () => api.examStart(examId!),
    onSuccess: (res) => {
      setSessionId(res.session.session_id);
      setQuestion(res.question);
      setQuestionNumber(res.question_number);
      setPhase("exam");
      setTimeLeft(res.session.time_remaining_seconds ?? null);
    },
    onError: () => back(),
  });

  useEffect(() => {
    // Pre-generated (agentic) session: load its first question directly.
    if (preSession) {
      void loadNext(preSession);
      return;
    }
    if (examId && !sessionId && !start.isPending && !start.isSuccess) {
      start.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId, preSession]);

  const sessionQuery = useQuery({
    queryKey: ["exam-session", sessionId],
    queryFn: () => api.examSession(sessionId!),
    enabled: !!sessionId && phase === "exam",
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (sessionQuery.data?.time_remaining_seconds != null) {
      setTimeLeft(sessionQuery.data.time_remaining_seconds);
    }
  }, [sessionQuery.data?.time_remaining_seconds]);

  useEffect(() => {
    if (timeLeft == null || phase !== "exam") return;
    const t = setInterval(() => {
      setTimeLeft((s) => (s != null && s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [phase, timeLeft != null]);

  const submit = useMutation({
    mutationFn: (answer: string) => {
      const elapsed = Math.round((Date.now() - questionStarted) / 1000);
      return api.examSubmitAnswer(sessionId!, answer, elapsed);
    },
    onSuccess: async (res) => {
      setFeedback({
        correct: res.correct,
        correctAnswer: res.correct_answer,
      });
      if (res.exam_complete) {
        const r = await api.examResults(sessionId!);
        useAttempts.getState().record(r); // local cache/fallback
        // Persist durably (Firestore if configured, else backend) — best-effort.
        const studentId = useSession.getState().user?.id;
        if (studentId) saveAttempt(studentId, r).catch(() => undefined);
        setResults(r);
        setPhase("results");
        return;
      }
      if (res.finished_section) {
        setSectionBreakLabel(res.session.section_label);
        setPhase("section_break");
        return;
      }
      // No auto-advance — let the student review / ask for an explanation,
      // then click "Next question".
    },
  });

  const explain = async () => {
    if (!question) return;
    setExplaining(true);
    try {
      const r = await api.explainQuestion({
        stem: question.stem,
        choices: question.choices,
        correct_answer: feedback?.correctAnswer ?? question.correct_answer,
        passage: question.passage,
        topic: question.topic,
        student_answer: selected ?? undefined,
      });
      setExplanation(r.explanation);
    } catch {
      setExplanation("Couldn't reach the tutor to explain this one — try again.");
    } finally {
      setExplaining(false);
    }
  };

  const loadNext = useCallback(async (sid: string) => {
    const res = await api.examCurrentQuestion(sid);
    setQuestion(res.question);
    setQuestionNumber(res.question_number);
    setSelected(null);
    setFeedback(null);
    setExplanation(null);
    setQuestionStarted(Date.now());
    setTimeLeft(res.session.time_remaining_seconds ?? null);
    setPhase("exam");
  }, []);

  const resumeSection = async () => {
    if (!sessionId) return;
    await api.examResume(sessionId);
    await loadNext(sessionId);
  };

  const progressPct = sessionQuery.data
    ? (sessionQuery.data.questions_answered / sessionQuery.data.total_questions) * 100
    : 0;

  if (start.isError) {
    return (
      <motion.div className="container py-16 text-center">
        <p className="text-destructive">Could not start exam.</p>
        <Button className="mt-4" onClick={back}>Back to mock exams</Button>
      </motion.div>
    );
  }

  if (phase === "loading" || start.isPending) {
    return (
      <motion.div className="container flex min-h-[50vh] items-center justify-center py-16">
        <p className="text-muted-foreground">Preparing your exam…</p>
      </motion.div>
    );
  }

  if (phase === "results" && results) {
    return <ResultsView results={results} onExit={back} />;
  }

  if (phase === "section_break") {
    return (
      <motion.div className="container max-w-lg py-20 text-center">
        <Badge className="mb-4">Section complete</Badge>
        <h2 className="font-serif text-3xl">Optional break</h2>
        <p className="mt-3 text-muted-foreground">
          You finished a section. On test day you may take one {10}-minute break between any
          sections. Next up: <strong>{sectionBreakLabel}</strong>
        </p>
        <Button className="mt-8 gap-2" onClick={() => void resumeSection()}>
          Continue to next section
          <ArrowRight className="size-4" />
        </Button>
      </motion.div>
    );
  }

  if (!question || !sessionQuery.data) return null;

  return (
    <motion.div className="container max-w-3xl space-y-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <motion.div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            {sessionQuery.data.section_label}
          </p>
          <p className="text-sm font-medium">
            Question {questionNumber} of {sessionQuery.data.total_questions}
          </p>
        </motion.div>
        {timeLeft != null && (
          <Badge variant={timeLeft < 60 ? "destructive" : "secondary"} className="gap-1 font-mono">
            <Clock className="size-3" />
            {formatTime(timeLeft)}
          </Badge>
        )}
      </div>

      <Progress value={progressPct} className="h-1.5" />

      <AnimatePresence mode="wait">
        <Card key={question.id}>
          <CardContent className="space-y-6 p-6">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">{question.sub_type}</Badge>
              <Badge variant="outline">{question.topic}</Badge>
              <Badge variant="outline">Difficulty {question.difficulty}</Badge>
            </div>

            {question.passage && (
              <p className="whitespace-pre-wrap rounded-lg border border-border/60 bg-secondary/30 p-4 font-mono text-xs leading-relaxed md:text-sm">
                {question.passage}
              </p>
            )}

            <p className="whitespace-pre-wrap text-base leading-relaxed">{question.stem}</p>

            <div className="space-y-2">
              {question.choices.map((c) => {
                const isSelected = selected === c.label;
                const showCorrect = feedback && feedback.correctAnswer === c.label;
                const showWrong = feedback && isSelected && !feedback.correct;
                return (
                  <button
                    key={c.label}
                    type="button"
                    disabled={!!feedback || submit.isPending}
                    onClick={() => setSelected(c.label)}
                    className={cn(
                      "flex w-full items-start gap-3 rounded-lg border p-4 text-left text-sm transition-colors",
                      isSelected && !feedback && "border-primary bg-primary/5",
                      showCorrect && "border-green-500/50 bg-green-500/10",
                      showWrong && "border-destructive/50 bg-destructive/10",
                    )}
                  >
                    <span className="font-semibold">{c.label}</span>
                    <span>{c.text}</span>
                  </button>
                );
              })}
            </div>

            {feedback && (
              <div
                className={cn(
                  "flex items-center gap-2 rounded-lg p-3 text-sm",
                  feedback.correct ? "bg-green-500/10 text-green-700 dark:text-green-400" : "bg-destructive/10 text-destructive",
                )}
              >
                {feedback.correct ? (
                  <CheckCircle2 className="size-4 shrink-0" />
                ) : (
                  <XCircle className="size-4 shrink-0" />
                )}
                {feedback.correct
                  ? "Correct"
                  : `Incorrect — answer ${feedback.correctAnswer ?? "—"}`}
              </div>
            )}

            {/* Maya's human explanation */}
            {feedback && (
              <div className="space-y-2">
                {!explanation && (
                  <Button
                    variant="outline"
                    className="w-full gap-2"
                    onClick={explain}
                    disabled={explaining}
                  >
                    <Sparkles className="size-4" />
                    {explaining ? "Maya is explaining…" : "Explain with Maya"}
                  </Button>
                )}
                {explanation && (
                  <div className="rounded-xl border border-border/60 bg-secondary/30 p-4">
                    <div className="mb-1.5 flex items-center gap-2 text-xs font-medium text-primary">
                      <span className="grid size-5 place-items-center rounded-full border border-border/60 bg-background font-serif italic">M</span>
                      Maya explains
                    </div>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">{explanation}</p>
                  </div>
                )}
              </div>
            )}

            <Button
              className="w-full"
              disabled={submit.isPending || (!feedback && !selected)}
              onClick={() => {
                if (feedback) loadNext(sessionId!);
                else if (selected) submit.mutate(selected);
              }}
            >
              {feedback ? "Next question" : "Submit answer"}
            </Button>
          </CardContent>
        </Card>
      </AnimatePresence>
    </motion.div>
  );
}

function ResultsView({ results, onExit }: { results: ExamResultsResponse; onExit: () => void }) {
  const setView = useWorkspace((s) => s.setView);
  const [low, high] = results.estimated_score_range;
  const fmtTime = (s: number) => `${Math.floor(s / 60)}m ${s % 60}s`;

  return (
    <motion.div className="container max-w-3xl space-y-10 py-10">
      <header className="text-center">
        <div className="flex items-center justify-center gap-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">
          <span className="hero-rule" /> Full analysis
        </div>
        <h1 className="mt-3 font-serif text-4xl md:text-5xl">{results.title}</h1>
        <p className="mt-3 font-serif text-6xl tabular-nums">
          {results.total_correct}
          <span className="text-2xl text-muted-foreground">/{results.total_questions}</span>
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {results.overall_percent}% correct · estimated Focus range{" "}
          <strong className="text-foreground">
            {low}–{high}
          </strong>
        </p>
      </header>

      {/* Section scores */}
      <section className="space-y-3">
        <h2 className="font-serif text-2xl">By section</h2>
        {results.section_scores.map((s) => (
          <div key={s.section_id} className="rounded-2xl border border-border/60 bg-card/60 p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">{s.label}</span>
              <span className="tabular-nums text-muted-foreground">
                {s.correct}/{s.total} · {s.percent}%
              </span>
            </div>
            <Bar pct={s.percent} />
          </div>
        ))}
      </section>

      {/* Topic breakdown */}
      {results.topic_breakdown.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-serif text-2xl">By topic</h2>
          <div className="grid gap-2 sm:grid-cols-2">
            {results.topic_breakdown.map((t) => (
              <div key={t.topic} className="rounded-xl border border-border/60 bg-card/60 p-3">
                <div className="flex items-center justify-between text-xs">
                  <span className="truncate font-medium">{t.topic}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {t.correct}/{t.total}
                  </span>
                </div>
                <Bar pct={t.percent} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Difficulty + timing */}
      <div className="grid gap-4 md:grid-cols-2">
        {results.difficulty_breakdown.length > 0 && (
          <Card className="border-border/60 bg-card/60">
            <CardContent className="space-y-3 p-5">
              <h3 className="font-serif text-xl">By difficulty</h3>
              {results.difficulty_breakdown.map((d) => (
                <div key={d.difficulty}>
                  <div className="flex items-center justify-between text-xs">
                    <span>Level {d.difficulty}</span>
                    <span className="tabular-nums text-muted-foreground">
                      {d.correct}/{d.total} · {d.percent}%
                    </span>
                  </div>
                  <Bar pct={d.percent} />
                </div>
              ))}
            </CardContent>
          </Card>
        )}
        {results.timing && (
          <Card className="border-border/60 bg-card/60">
            <CardContent className="space-y-2 p-5 text-sm">
              <h3 className="font-serif text-xl">Timing</h3>
              <Row label="Total time" value={fmtTime(results.timing.total_seconds)} />
              <Row label="Avg / question" value={`${results.timing.avg_seconds}s`} />
              <Row label="Fastest" value={`${results.timing.fastest_seconds}s`} />
              <Row label="Slowest" value={`${results.timing.slowest_seconds}s`} />
              {results.timing.slowest_topic && (
                <Row label="Slowest on" value={results.timing.slowest_topic} />
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Strengths / weaknesses */}
      <div className="grid gap-4 md:grid-cols-2">
        {results.strengths.length > 0 && (
          <div className="rounded-2xl border border-success/40 bg-success/5 p-4">
            <p className="text-sm font-medium text-success">Strengths</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {results.strengths.map((t) => (
                <Badge key={t} variant="secondary">{t}</Badge>
              ))}
            </div>
          </div>
        )}
        {results.weak_topics.length > 0 && (
          <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-4">
            <p className="text-sm font-medium text-destructive">Focus next on</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {results.weak_topics.map((t) => (
                <Badge key={t} variant="secondary">{t}</Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
        <Button variant="default" onClick={() => setView("tutor")}>
          Ask Maya to review mistakes
        </Button>
        <Button variant="outline" onClick={() => setView("dashboard")}>
          View my dashboard
        </Button>
        <Button variant="secondary" onClick={onExit}>
          Back to mock exams
        </Button>
      </div>
    </motion.div>
  );
}

function Bar({ pct }: { pct: number }) {
  const color = pct < 50 ? "bg-destructive" : pct < 75 ? "bg-accent" : "bg-success";
  return (
    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-secondary">
      <div className={cn("h-full transition-all", color)} style={{ width: `${pct}%` }} />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  );
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
